import {
  addToast,
  Input,
  Button,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { getAuth } from "firebase/auth";
import * as React from "react";
import { useState } from "react";
import useAuthStore from "../store/authStore";
import { useApi } from "../hooks/useApi";
import { useTranslation } from "react-i18next";

export interface DictionaryResponse {
  translations: string[];
  synonyms: string[];
}

export const DictionaryPage: React.FC = () => {
  const [searchWord, setSearchWord] = useState<string>("");
  const [dictionaryData, setDictionaryData] = useState<DictionaryResponse | null>(null);
  const { loading, post } = useApi();
  const { t } = useTranslation();

  const fetchDictionaryData = async (text: string, direction: number): Promise<void> => {
    const payload = { text, direction };
    const data: DictionaryResponse = await post("/translate", payload);

    setDictionaryData(data);
  };

  const onSubmit = () => {
    if (searchWord.trim() !== "") {
      fetchDictionaryData(searchWord.trim(), 1);
    }
  };

  const topContent = () => {
    return (
      <div className="w-full">
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={searchWord}
            onChange={(e) => setSearchWord(e.target.value)}
            onClear={() => {
              setSearchWord("");
              setDictionaryData(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSubmit();
              }
            }}
            isClearable
            startContent={<Icon icon="iconoir:search" />}
            placeholder={t("searchPlaceholder")}
            className="flex-1"
            classNames={{
              inputWrapper: "bg-white",
            }}
          />
          <Button onPress={onSubmit} color="primary" variant="shadow" type="submit">
            {t("search")}
          </Button>
        </div>
        <div className="flex text-sm font-semibold text-zinc-400 h-6 mt-2 ">
          <span>{`Нийт илэрц: ${dictionaryData ? dictionaryData.translations.length : 0}`}</span>
        </div>
      </div>
    );
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    addToast({
      title: t("copied", { copied: text }),
      severity: "success",
      color: "success",
      shouldShowTimeoutProgress: true,
      timeout: 2000,
    });
  };

  return (
    <>
      <Table
        isCompact
        removeWrapper
        aria-label="Dictionary table"
        topContent={topContent()}
        className="table-fixed w-full"
        classNames={{
          th: "bg-white",
        }}
      >
        <TableHeader>
          <TableColumn className="w-[10%]">№</TableColumn>
          <TableColumn className="w-[80%]">{t("searchResults")}</TableColumn>
          <TableColumn className="w-[10%] text-center"> </TableColumn>
        </TableHeader>
        <TableBody
          isLoading={loading}
          loadingContent={
            <div className="text-center p-4">
              <Spinner color="default" variant="wave" />
            </div>
          }
          emptyContent={
            !loading && dictionaryData === null ? (
              <div className="text-center p-4 text-default-400">{t("noInputToSearch")}</div>
            ) : !loading && dictionaryData?.translations.length === 0 ? (
              <div className="text-center p-4 text-default-400">{t("noSearchResults")}</div>
            ) : null
          }
        >
          {!loading &&
            dictionaryData &&
            dictionaryData.translations.map((translation, index) => (
              <TableRow key={index}>
                <TableCell className="w-[10%]">{index + 1}</TableCell>
                <TableCell className="w-[80%] break-words">{translation}</TableCell>
                <TableCell className="w-[10%] text-center">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={() => handleCopy(translation)}
                  >
                    <Icon icon="mdi:content-copy" className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </>
  );
};
